'use client';
import { FC, ReactNode } from 'react';
import { Typography, Dialog, DialogActions, DialogContent, DialogTitle, Button } from '@mui/material';
import { SplitDialog } from './AddSplitDialog';
import { AddTransferActivityDialog } from './AddTransferActivityDialog';
import { AddProjectActivityDialog } from './AddProjectActivityDialog';
import { AddCommunicationDialog } from './AddCommunicationDialog';
import { AddShippingDialog } from './AddShippingDialog';
import { AddStorageDialog } from './AddStorageDialog';
import { AddSimulationDatasetDialog } from './AddSimulationDatasetDialog';
import { AddDataPackageDialog } from './AddDataPackageDialog';
import { AddCharacterizationDialog } from './AddCharacterizationDialog';
import { AddProcessingDialog } from './AddProcessingDialog';
import { MultiSampleGrowthDialog } from './MultiSampleGrowthDialog';
import { CombineStartingMaterialsDialog } from './CombineStartingMaterialsDialog';
import { AddToInventoryDialog } from './AddToInventoryDialog';
import { AddProjectDialog } from './AddProjectDialog';
import { AddTheoryActivityDialog } from './AddTheoryActivityDialog';
import { SampleSelectionDialogWrapper } from './SampleSelectionDialogWrapper';
import { TheoryActivitySelectionDialogWrapper } from './TheoryActivitySelectionDialogWrapper';
import { ProjectSelectionDialogWrapper } from './ProjectSelectionDialogWrapper';
import { SetDoiDialog } from './SetDoiDialog';
import { DownloadFolderDialog } from './DownloadFolderDialog';
import { BulkRecipeEditorDialog } from './BulkRecipeEditorDialog';
import { BulkTransferDialog } from './BulkTransferDialog';
import { NewBulkGrowthDialog } from './NewBulkGrowthDialog';
import { NewBulkSampleDialog } from './NewBulkSampleDialog';
import { NewBulkInstrumentRunDialog } from './NewBulkInstrumentRunDialog';
import { CreateShippingDialog } from './CreateShippingDialog';
import { BulkShippingCreatedDialog } from './BulkShippingCreatedDialog';
import { CreatePublicationDialog } from './CreatePublicationDialog';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useDialogStore } from '@/stores/dialogStore';

/**
 * Props for the DialogProvider component
 */
interface DialogProviderProps {
  /** Child components that will have access to dialogs */
  children: ReactNode;
}

/**
 * DialogProvider component mounts all dialog components and makes them
 * available throughout the application. This ensures dialogs are mounted
 * only once and can be triggered from anywhere using the dialog store.
 */
export const DialogProvider: FC<DialogProviderProps> = ({ children }) => {
  const {
    // Global dialog states
    projectDialog,
    confirmDialog,
    inventoryDialog,
    combineStartingMaterialsDialog,
    multiSampleGrowthDialog,
    processingDialog,
    characterizationDialog,
    splitDialog,
    storageDialog,
    transferActivityDialog,
    projectActivityDialog,
    createPublicationDialog,
    communicationDialog,
    shippingDialog,
    addTheoryActivityDialog,
    addSimulationDatasetDialog,
    addDataPackageDialog,
    submitDialog,
    resetDialog,
    deleteDialog,
    doiDialog,
    downloadFolderDialog,
    bulkSampleDialog,
    bulkTransferDialog,
    bulkGrowthDialog,
    // Actions
    closeProjectDialog,
    closeConfirmDialog,
    closeInventoryDialog,
    closeSampleFromStartingMaterialsDialog,
    closeMultiSampleGrowthDialog,
    closeProcessingDialog,
    closeCharacterizationDialog,
    closeSplitDialog,
    closeStorageDialog,
    closeTransferActivityDialog,
    closeProjectActivityDialog,
    closeCreatePublicationDialog,
    closeCommunicationDialog,
    closeShippingDialog,
    closeAddTheoryActivityDialog,
    closeAddSimulationDatasetDialog,
    closeAddDataPackageDialog,
    closeSubmitDialog,
    closeResetDialog,
    closeDeleteDialog,
    closeDoiDialog,
    closeDownloadFolderDialog,
  } = useDialogStore();

  return (
    <>
      {children}

      {/* Global Project Dialog */}
      <AddProjectDialog
        open={projectDialog.isOpen}
        onClose={() => closeProjectDialog()}
        onSubmit={(project) => closeProjectDialog(project)}
      />

      {/* Global Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        isDestructive={confirmDialog.isDestructive}
        onConfirm={() => closeConfirmDialog(true)}
        onClose={() => closeConfirmDialog(false)}
      />

      {/* Global Inventory Dialog */}
      <AddToInventoryDialog
        key={inventoryDialog.project?.id}
        open={inventoryDialog.isOpen}
        project={inventoryDialog.project}
        onClose={() => closeInventoryDialog()}
        onInventoryComplete={(sample) => closeInventoryDialog(sample)}
      />

      {/* Combine Starting Materials Dialog */}
      <CombineStartingMaterialsDialog
        open={combineStartingMaterialsDialog.isOpen}
        project={combineStartingMaterialsDialog.project}
        onClose={() => closeSampleFromStartingMaterialsDialog()}
      />

      {/* Multi-sample growth dialog */}
      <MultiSampleGrowthDialog
        open={multiSampleGrowthDialog.isOpen}
        project={multiSampleGrowthDialog.project}
        onClose={() => closeMultiSampleGrowthDialog()}
        onSubmit={(result) => closeMultiSampleGrowthDialog(result)}
      />

      {/* Multi-sample growth dialog */}
      <MultiSampleGrowthDialog
        open={multiSampleGrowthDialog.isOpen}
        project={multiSampleGrowthDialog.project}
        onClose={() => closeMultiSampleGrowthDialog()}
        onSubmit={(result) => closeMultiSampleGrowthDialog(result)}
      />

      {/* Global Processing Dialog */}
      {processingDialog.sample ? (
        <AddProcessingDialog
          open={processingDialog.isOpen}
          onClose={() => closeProcessingDialog()}
          sample={processingDialog.sample}
          prefill={processingDialog.prefill}
          onSubmit={(activity) => closeProcessingDialog(activity)}
        />
      ) : null}

      {/* Global Characterization Dialog */}
      {characterizationDialog.sample ? (
        <AddCharacterizationDialog
          key={characterizationDialog.sample.id ?? 'characterization-sample'}
          open={characterizationDialog.isOpen}
          onClose={() => closeCharacterizationDialog()}
          sample={characterizationDialog.sample}
          parentActivity={characterizationDialog.parentActivity}
          onSubmit={(activity) => closeCharacterizationDialog(activity)}
        />
      ) : null}

      {/* Global Split Dialog */}
      <SplitDialog
        open={splitDialog.isOpen}
        onClose={() => closeSplitDialog()}
        sample={splitDialog.sample!}
        onSubmit={(activity) => closeSplitDialog(activity)}
      />

      {/* Global Storage Dialog */}
      <AddStorageDialog
        open={storageDialog.isOpen}
        onClose={() => closeStorageDialog()}
        sample={storageDialog.sample!}
        onSubmit={(activity) => closeStorageDialog(activity)}
      />

      {/* Global Transfer Activity Dialog — mount only when open (avoids sample-scoped hooks on app load) */}
      {transferActivityDialog.isOpen && transferActivityDialog.sample ? (
        <AddTransferActivityDialog
          open={transferActivityDialog.isOpen}
          onClose={() => closeTransferActivityDialog()}
          sample={transferActivityDialog.sample}
          isCurrentProjectInHouse={transferActivityDialog.isCurrentProjectInHouse}
          onSubmit={(projectIds) => closeTransferActivityDialog(projectIds)}
        />
      ) : null}

      {/* Global Project Activity Dialog */}
      <AddProjectActivityDialog
        open={projectActivityDialog.isOpen}
        onClose={() => closeProjectActivityDialog()}
        project={projectActivityDialog.project!}
        activityType={projectActivityDialog.activityType}
        onSubmit={(activity) => closeProjectActivityDialog(activity)}
      />

      <CreatePublicationDialog
        key={`${createPublicationDialog.isOpen ? 'open' : 'closed'}-${createPublicationDialog.project?.id ?? 'none'}`}
        open={createPublicationDialog.isOpen}
        project={createPublicationDialog.project}
        onClose={(result) => closeCreatePublicationDialog(result ?? undefined)}
      />

      {/* Global Communication Dialog */}
      <AddCommunicationDialog
        open={communicationDialog.isOpen}
        onClose={() => closeCommunicationDialog()}
        project={communicationDialog.project!}
        onSubmit={(communication) => closeCommunicationDialog(communication)}
      />

      {/* Global Shipping Dialog */}
      {shippingDialog.isOpen ? (
        <AddShippingDialog
          key={shippingDialog.project?.id ?? 'add-shipping-no-project'}
          open={shippingDialog.isOpen}
          onClose={() => closeShippingDialog()}
          project={shippingDialog.project}
          returnAddress={shippingDialog.returnAddress}
          launchFrom={shippingDialog.launchFrom ?? 'projectTree'}
          onSubmit={(shipping) => closeShippingDialog(shipping)}
        />
      ) : null}

      {/* Global Add Theory Activity Dialog */}
      <AddTheoryActivityDialog
        open={addTheoryActivityDialog.isOpen}
        onClose={() => closeAddTheoryActivityDialog()}
        onSubmit={(activity) => closeAddTheoryActivityDialog(activity)}
        dataPackageId={addTheoryActivityDialog.dataPackage?.id || 0}
      />

      {/* Global Add Simulation Dataset Dialog */}
      <AddSimulationDatasetDialog
        open={addSimulationDatasetDialog.isOpen}
        onClose={() => closeAddSimulationDatasetDialog()}
        onSubmit={(data) => closeAddSimulationDatasetDialog(data)}
        projectId={addSimulationDatasetDialog.project?.id?.toString() || ''}
      />

      {/* Global Add Data Package Dialog */}
      <AddDataPackageDialog
        open={addDataPackageDialog.isOpen}
        onClose={() => closeAddDataPackageDialog()}
        onSubmit={(data) => closeAddDataPackageDialog(data)}
      />

      {/* Global Sample Selection Dialog */}
      <SampleSelectionDialogWrapper />

      {/* Global Theory Activity Selection Dialog */}
      <TheoryActivitySelectionDialogWrapper />

      {/* Global Project Selection Dialog */}
      <ProjectSelectionDialogWrapper />

      {/* Global Submit Dialog */}
      <Dialog open={submitDialog.isOpen} onClose={() => closeSubmitDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Submit {submitDialog.nodeType} {submitDialog.nodeId}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Do you want to submit this {submitDialog.nodeType?.toLowerCase()} now?
          </Typography>
          <Typography variant="body2">
            <strong>Submitted {submitDialog.nodeType?.toLowerCase()}s cannot be deleted any more</strong> (except by an
            administrator).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => closeSubmitDialog(true)} color="primary" variant="contained">
            Yes
          </Button>
          <Button onClick={() => closeSubmitDialog(false)} color="primary" variant="outlined">
            No
          </Button>
        </DialogActions>
      </Dialog>

      {/* Global Reset Dialog */}
      <Dialog open={resetDialog.isOpen} onClose={() => closeResetDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reset this entry</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Do you want to reset {resetDialog.nodeType} {resetDialog.nodeId} to Pending?
          </Typography>
          <Typography variant="body2">
            This will revert the entry to a modifiable state. You can submit it again after editing.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => closeResetDialog(true)} color="warning" variant="contained">
            Yes
          </Button>
          <Button onClick={() => closeResetDialog(false)} color="primary" variant="outlined">
            No
          </Button>
        </DialogActions>
      </Dialog>

      {/* Global Delete Dialog */}
      <Dialog open={deleteDialog.isOpen} onClose={() => closeDeleteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete this entry</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Do you want to delete {deleteDialog.nodeType} {deleteDialog.nodeId}?
          </Typography>
          <Typography variant="body2">This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => closeDeleteDialog(true)} color="error" variant="contained">
            Yes
          </Button>
          <Button onClick={() => closeDeleteDialog(false)} color="primary" variant="outlined">
            No
          </Button>
        </DialogActions>
      </Dialog>

      {/* Global DOI Dialog */}
      <SetDoiDialog
        key={doiDialog.dataPackage?.id}
        open={doiDialog.isOpen}
        dataPackage={doiDialog.dataPackage}
        onClose={(result) => closeDoiDialog(result)}
      />

      {/* Global Share Folder Dialog */}
      {downloadFolderDialog.folderName && downloadFolderDialog.initialLinkResult ? (
        <DownloadFolderDialog
          key={`${downloadFolderDialog.folderName}-${downloadFolderDialog.initialLinkResult.link}`}
          open={downloadFolderDialog.isOpen}
          folderName={downloadFolderDialog.folderName}
          initialLinkResult={downloadFolderDialog.initialLinkResult}
          onClose={() => closeDownloadFolderDialog()}
          onPollForUpdates={downloadFolderDialog.onPollForUpdates}
        />
      ) : null}

      {/* Global Bulk Recipe Editor Dialog */}
      <BulkRecipeEditorDialog />

      {/* Global Bulk Transfer Dialog */}
      <BulkTransferDialog
        key={`${bulkTransferDialog.isOpen ? 'open' : 'closed'}-${bulkTransferDialog.samples?.length || 0}-${bulkTransferDialog.isCurrentProjectInHouse || ''}`}
      />

      {/* Global Bulk Growth Dialog */}
      <NewBulkGrowthDialog
        key={`${bulkGrowthDialog.isOpen ? 'open' : 'closed'}-${bulkGrowthDialog.samples?.length || 0}-${bulkGrowthDialog.initialProject?.id || ''}-${bulkGrowthDialog.initialTechnique || ''}-${bulkGrowthDialog.title || ''}`}
      />

      {/* Global Bulk Sample Dialog */}
      <NewBulkSampleDialog
        key={`${bulkSampleDialog.isOpen ? 'open' : 'closed'}-${bulkSampleDialog.initialProject?.id || ''}-${bulkSampleDialog.initialRecipeTemplate || ''}`}
      />

      {/* Global Bulk Instrument Run Dialog */}
      <NewBulkInstrumentRunDialog />

      {/* Global Bulk Shipping Dialog */}
      <CreateShippingDialog />
      <BulkShippingCreatedDialog />
    </>
  );
};
